import { CHAT_ASSISTANT_PADDING_HORIZONTAL } from '@fruitful/agent-ui';
import {
    buildMultiSelectConfirmedAgentMessage,
    type ChatMessage as ChatMessageType,
    CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY,
    formatUserMessageForDisplay,
    getActiveDraftedMessageButtonWidget,
    getActiveEstimatedBillsWidget,
    getActiveSavingsGoalAmountsWidget,
    getAssistantDisplayContent,
    getBranchOpener,
    getLatestAssistantMessageLogoIconTarget,
    GOALS_SYSTEM_WELCOME_OPTION_ID,
    MULTI_SELECT_CONFIRMED_PREFIX,
    parseMultiSelectConfirmedMessage,
    resolveWelcomeBranch,
    type SendSilentMessageOptions,
    type ThreadMetricsSurfaceState,
} from '@fruitful/llm-service-shared';
import {
    AnalyticsUserEvent,
    CMMBranch,
    OnboardingChatModule,
    type WelcomeSelection,
    WidgetType,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Keyboard,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    type ScrollView,
} from 'react-native';
import Animated, {
    Easing,
    type SharedValue,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    BodySmallRegular,
    H3Regular,
    isNonMobileWeb,
    LabelRegular,
    View,
    YStack,
} from 'smoothie';

import { StorageKeys } from '../../common/constants';
import storage from '../../common/providers/StorageFacade';
import { FRUITFUL_ENV } from '../../config/config';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { track, trackGrowthMarketingEvent } from '../../logger/Logger';
import { useAiChatRenderAnalytics } from '../hooks/useAiChatRenderAnalytics';
import { usePreRenderedWidgets } from '../hooks/usePreRenderedWidgets';
import { useTurnAnchoredScroll } from '../hooks/useTurnAnchoredScroll';
import {
    isBuildingPlanAtom,
    isChatFadingOutAtom,
    isChatInputLockedAtom,
    isPlanReadyAtom,
    welcomeSelectionAtom,
} from '../state/OnboardingChatAtoms';
import { preloadGoalCardImages } from '../utils/preloadGoalCardImages';
import { clearScheduledCmmAutoNavigate } from './ChatWidgets';
import { MultiSelectInputWidget } from './MultiSelectInputWidget';
import { OnboardingChatProgressBar } from './OnboardingChatProgressBar';
import { AssistantMessage } from './shared-chat/AssistantMessage';
import { ChatInput, ONBOARDING_AI_DISCLOSURE } from './shared-chat/ChatInput';
import { ChatMessageLayout } from './shared-chat/ChatMessageLayout';
import { type ChatWidgetHandlers } from './shared-chat/ChatWidgetRenderer';
import NewChatButton from './shared-chat/NewChatButton';
import { ScrollToBottomButton } from './shared-chat/ScrollToBottomButton';
import { SequentialAssistantContent } from './shared-chat/SequentialAssistantContent';
import { TypingIndicator } from './shared-chat/TypingIndicator';
import { UserMessageBubble } from './shared-chat/UserMessageBubble';
import WelcomeAnimation from './shared-chat/WelcomeAnimation';
import { SliderInputWidget } from './SliderInputWidget';
import {
    WELCOME_INTRO_MESSAGE,
    WELCOME_START_MENU_OPTIONS,
    WelcomeStartMenu,
} from './welcome-carousel/WelcomeStartMenu';
import { ConfirmationMenuWidget } from './widgets/ConfirmationMenuWidget';
import { DebtBalanceSlidersWidget } from './widgets/DebtBalanceSlidersWidget';
import { EstimatedBillsWidget } from './widgets/EstimatedBillsWidget';
import { IncomeCollectionWidget } from './widgets/IncomeCollectionWidget';
import { SavingsGoalAmountsWidget } from './widgets/SavingsGoalAmountsWidget';
import { SavingsGoalsWidget } from './widgets/SavingsGoalsWidget';
import { SingleSelectWidget } from './widgets/SingleSelectWidget';

const trackSelectInputSubmitted = (
    responseKey: string,
    agentPayload: string,
    // A pure single-select (one option, no free-text) auto-submits on tap —
    // matches the widget's own `autoSubmitOnSelect`. Emit a single-select event
    // for those instead of the multi-select one so they aren't mislabeled.
    isSingleSelect: boolean,
) => {
    const payload = parseMultiSelectConfirmedMessage(agentPayload);

    track(
        isSingleSelect
            ? AnalyticsUserEvent.CMM_INPUT_SINGLE_SELECT_SUBMITTED
            : AnalyticsUserEvent.CMM_INPUT_MULTI_SELECT_SUBMITTED,
        {
            responseKey,
            selectedOptionIds: payload?.selectedOptionIds.join(',') ?? '',
            hasCustomInput: (payload?.inputText ?? '').trim() !== '',
            responseCustomInput: (payload?.inputText ?? '').trim(),
        },
    );
};

/** URL param that deep-links straight past the first question with an answer. */
export const MONEY_GOAL_PARAM = 'money-goal';

/**
 * Turns a `money-goal` deep-link value into the answer text we treat as the
 * member's first-question response. Matches a known menu option (by id or
 * label, case-insensitive); otherwise humanizes the raw value (`pay-off-debt`
 * → `Pay off debt`) so a slug still reads well in the chat bubble.
 */
const resolveMoneyGoalAnswer = (raw: string): string => {
    const lower = raw.trim().toLowerCase();
    const match = WELCOME_START_MENU_OPTIONS.find(
        (option) => option.id === lower || option.label.toLowerCase() === lower,
    );
    if (match != null) return match.label;

    const humanized = raw.replace(/[-_]+/g, ' ').trim();
    if (humanized === '') return raw.trim();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

/**
 * Resolves a `money-goal` deep-link value to a known welcome option id (by id or
 * label, case-insensitive) so it drives the same per-selection opener intro as
 * the menu. Returns undefined for values that don't match a menu option.
 */
const resolveMoneyGoalOptionId = (raw: string): string | undefined => {
    const lower = raw.trim().toLowerCase();
    return WELCOME_START_MENU_OPTIONS.find(
        (option) => option.id === lower || option.label.toLowerCase() === lower,
    )?.id;
};

/**
 * Injects `resolvedBranch` into a welcome multi-select payload so the agent
 * receives a deterministic branch assignment instead of reasoning about it.
 */
const enrichWelcomePayload = (agentPayload: string): string => {
    const parsed = parseMultiSelectConfirmedMessage(agentPayload);
    if (parsed == null) return agentPayload;

    const branch = resolveWelcomeBranch(parsed.selectedOptionIds[0]);
    if (branch == null) return agentPayload;

    const displayText = agentPayload
        .slice(0, agentPayload.indexOf(MULTI_SELECT_CONFIRMED_PREFIX))
        .trimEnd();

    const enrichedPayload = { ...parsed, resolvedBranch: branch };
    return `${displayText}\n${MULTI_SELECT_CONFIRMED_PREFIX} ${JSON.stringify(enrichedPayload)}`;
};

/**
 * Deterministic first-question answer for the results object / artifact. A menu
 * pick (or resolved deep link) carries `optionId` + `label`; a free-text
 * ("Something else…") answer carries `freeText`.
 */
const buildWelcomeSelection = (
    answer: string,
    welcomeOptionId: string | undefined,
): WelcomeSelection => {
    const trimmed = answer.trim();
    if (welcomeOptionId != null) {
        return { optionId: welcomeOptionId, label: trimmed, freeText: null };
    }
    return { optionId: null, label: null, freeText: trimmed };
};

/**
 * Re-derives the welcome selection from a restored thread. The optimistic live
 * message only stores display text, so this reads the persisted `memberNotes`
 * payload (which carries `welcomeOptionId` + `inputText`) after a refresh.
 */
const deriveWelcomeSelectionFromMessages = (
    messages: ChatMessageType[],
): WelcomeSelection | null => {
    for (const message of messages) {
        if (message.role !== 'user') continue;
        const text = message.content
            .map((c) => (c.type === 'text' ? (c.text ?? '') : ''))
            .join('');
        const parsed = parseMultiSelectConfirmedMessage(text);
        if (parsed?.responseKey === CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY) {
            return buildWelcomeSelection(
                parsed.inputText ?? '',
                parsed.welcomeOptionId,
            );
        }
    }
    return null;
};

const CHAT_FADE_OUT_MS = 800;
const SCROLL_UP_THRESHOLD_PX = 200;
const SCROLL_UP_DEBOUNCE_MS = 500;
const SCROLL_TO_BOTTOM_THRESHOLD_PX = 24;
const SCROLL_TO_BOTTOM_BUTTON_GAP = 16;
const SCROLL_UP_DETECTION_PX = 2;
/** Matches TYPE_TARGET_TOTAL_MS in UserMessageBubble — the user bubble needs
 *  this long to finish its character typewriter before the synthetic opener mounts. */
const BUBBLE_TYPEWRITER_MS = 700;
const INPUT_WIDGET_REVEAL_DELAY_MS = 800;
/** After bills confirm, wait this long (post-stream) before nudging show-intake-summary. */
const BUILDING_PLAN_STUCK_MS = 12_000;

const BUILDING_PLAN_RECOVERY_MESSAGE =
    'The member already confirmed their bills. Do NOT ask them anything. Immediately call submit-core-numbers if not already submitted, then call show-intake-summary with autoNavigate: true and the full intake (including branch, slides, slideText, and goals data). Do not stream a closing message.';

const hasAutoNavigateAhaMoment = (messages: ChatMessageType[]): boolean =>
    messages.some(
        (msg) =>
            msg.role === 'assistant' &&
            getAssistantDisplayContent(msg).some(
                (part) =>
                    part.type === 'ui' &&
                    part.widget === WidgetType.AhaMoment &&
                    (part.props as Record<string, unknown>)?.autoNavigate ===
                        true,
            ),
    );

interface OnboardingChatContainerProps {
    messages: ChatMessageType[];
    isStreaming: boolean;
    isRestoringHistory: boolean;
    showTypingIndicator?: boolean;
    disabled?: boolean;
    threadMetadata?: ThreadMetricsSurfaceState | null;
    onSendMessage: (message: string) => void;
    onSendMessageWithAgentPayload: (
        displayText: string,
        agentPayload: string,
    ) => Promise<void>;
    /** Classify a free-text savings-goal entry before it becomes a pill. */
    onClassifyCustomSavingsGoal?: (
        inputText: string,
    ) => Promise<{ isGoal: boolean; label: string | null }>;
    onSetBranch: (
        displayText: string,
        agentPayload: string,
        branch: string,
        options?: { allowEmptyDisplay?: boolean },
    ) => Promise<void>;
    onSendSilentMessage: (
        message: string,
        options?: SendSilentMessageOptions,
    ) => Promise<void>;
    onStopStreaming?: () => void;
    onAddCollectedModule?: (module: OnboardingChatModule) => void;
    onResetThread?: () => void;
    /** Fired once the `money-goal` deep-link has been read + stored, so the page
     *  can strip the param from the URL. */
    onDeepLinkConsumed?: () => void;
}

export const OnboardingChatContainer = ({
    messages,
    isStreaming,
    isRestoringHistory,
    showTypingIndicator = false,
    disabled = false,
    threadMetadata,
    onSendMessage,
    onSendMessageWithAgentPayload,
    onClassifyCustomSavingsGoal,
    onSetBranch,
    onSendSilentMessage,
    onStopStreaming,
    onAddCollectedModule,
    onResetThread,
    onDeepLinkConsumed,
}: OnboardingChatContainerProps) => {
    const params = useLocalSearchParams();

    // Deep link: `?money-goal=…` is treated as the answer to the first
    // ("what best describes you?") question — we auto-answer it and drop the
    // member straight into partner planning, skipping the welcome menu.
    const moneyGoalRaw = params[MONEY_GOAL_PARAM];
    const moneyGoalParam = Array.isArray(moneyGoalRaw)
        ? moneyGoalRaw[0]
        : moneyGoalRaw;
    const hasMoneyGoalDeepLink =
        typeof moneyGoalParam === 'string' && moneyGoalParam.trim() !== '';
    const autoAnsweredMoneyGoalRef = useRef(false);
    // Sticky flag so the welcome menu stays suppressed even after the deep-link
    // param is stripped from the URL (see the auto-answer effect below).
    const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);
    const scrollViewRef = useRef<ScrollView>(null);
    const setIsChatInputLocked = useSetAtom(isChatInputLockedAtom);
    const setIsChatFadingOut = useSetAtom(isChatFadingOutAtom);
    const isChatFadingOut = useAtomValue(isChatFadingOutAtom);
    const isBuildingPlan = useAtomValue(isBuildingPlanAtom);
    const setIsBuildingPlan = useSetAtom(isBuildingPlanAtom);
    const isPlanReady = useAtomValue(isPlanReadyAtom);
    const setIsPlanReady = useSetAtom(isPlanReadyAtom);
    const setWelcomeSelection = useSetAtom(welcomeSelectionAtom);
    // Guards the one-time restore hydration of the welcome selection (see effect
    // below); also reset by "Start over" so a new thread re-derives.
    const didHydrateWelcomeSelectionRef = useRef(false);
    const {
        footerWidgetProps: preRenderedWidgetProps,
        footerSliderProps,
        footerDebtBalanceSlidersProps,
        footerIncomeCollectionProps,
        syntheticOpenerMessage,
        openerActive,
        onWelcomeSubmit,
        onFooterWidgetSubmit,
    } = usePreRenderedWidgets(messages, isStreaming);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const isKeyboardVisible = keyboardHeight > 0;
    const maxScrollOffsetRef = useRef(0);
    const pendingScrollUpPxRef = useRef(0);
    const pendingScrollOffsetRef = useRef(0);
    const scrollUpDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const scrollMetricsRef = useRef({
        offsetY: 0,
        contentHeight: 0,
        viewportHeight: 0,
    });
    const suppressScrollToBottomButtonRef = useRef(false);
    const [showScrollToBottomButton, setShowScrollToBottomButton] =
        useState(false);
    const [footerHeight, setFooterHeight] = useState(0);
    // Height of just the AI disclosure line, so only it (not the whole footer) is reserved.
    const [disclosureHeight, setDisclosureHeight] = useState(0);
    // Keeps the welcome carousel mounted through its tap -> emphasis -> exit
    // choreography even though `showStaticWelcome` flips false the instant
    // the pick is sent as a real chat message.
    const [isHandingOffWelcome, setIsHandingOffWelcome] = useState(false);
    // Hides the first answer bubble until its anchor scroll settles.
    const [firstAnswerRevealed, setFirstAnswerRevealed] = useState(false);
    // True once a card has actually been tapped this session — gates the
    // typewriter effect so restored sessions never re-type history.
    const hasPickedBranchRef = useRef(false);
    const [syntheticOpenerRevealed, setSyntheticOpenerRevealed] =
        useState(false);
    /** Delays the synthetic opener mount until the user bubble finishes typing. */
    const [syntheticContentReady, setSyntheticContentReady] = useState(false);
    const [assistantContentRevealed, setAssistantContentRevealed] =
        useState(true);
    const [inputWidgetReady, setInputWidgetReady] = useState(true);

    const { value: useBillsWidget } = useFeatureGate(
        FeatureFlags.ENABLE_CMM_EDITABLE_BILLS_WIDGET,
    );
    const { value: useSavingsGoalsWidget } = useFeatureGate(
        FeatureFlags.ENABLE_CMM_EDITABLE_SAVINGS_GOALS_WIDGET,
    );

    useEffect(() => {
        if (!assistantContentRevealed) {
            setInputWidgetReady(false);
            return undefined;
        }
        const timer = setTimeout(() => {
            setInputWidgetReady(true);
        }, INPUT_WIDGET_REVEAL_DELAY_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [assistantContentRevealed]);

    const wasStreamingRef = useRef(isStreaming);
    const wasOpenerActiveRef = useRef(openerActive);
    useEffect(() => {
        const startedStreaming = isStreaming && !wasStreamingRef.current;
        const startedOpener = openerActive && !wasOpenerActiveRef.current;
        if (startedStreaming || startedOpener) {
            setAssistantContentRevealed(false);
        }
        wasStreamingRef.current = isStreaming;
        wasOpenerActiveRef.current = openerActive;
    }, [isStreaming, openerActive]);

    const lastMessage = messages[messages.length - 1];
    const lastMessageHasNoVisibleContent =
        lastMessage?.role === 'assistant' &&
        getAssistantDisplayContent(lastMessage).length === 0;
    useEffect(() => {
        if (lastMessageHasNoVisibleContent && !isStreaming && !openerActive) {
            setAssistantContentRevealed(true);
        }
    }, [lastMessageHasNoVisibleContent, isStreaming, openerActive]);

    // On a restored session, mark the opener AND assistant content as already
    // revealed so the footer widget isn't gated. syntheticOpenerMessage starts
    // null (set via useEffect in the hook) so the useState initializer can't
    // detect it. Without `setAssistantContentRevealed(true)` here, the
    // transition-detection effect above clears it when openerActive flips on,
    // and the onAllPartsRevealed callback from SequentialAssistantContent
    // doesn't fire reliably under skipTypingAnimation on restore.
    useEffect(() => {
        if (
            syntheticOpenerMessage != null &&
            !hasPickedBranchRef.current &&
            messages.length > 0
        ) {
            setSyntheticOpenerRevealed(true);
            setAssistantContentRevealed(true);
        }
    }, [syntheticOpenerMessage, messages.length]);

    useEffect(() => {
        if (
            syntheticContentReady ||
            syntheticOpenerMessage == null ||
            isHandingOffWelcome
        ) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setSyntheticContentReady(true);
        }, BUBBLE_TYPEWRITER_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [syntheticOpenerMessage, isHandingOffWelcome, syntheticContentReady]);

    const hasLoggedFirstQuestionViewRef = useRef(false);
    const { trackAgentResponsePartRendered } = useAiChatRenderAnalytics(
        messages,
        isRestoringHistory,
    );

    const [mobileWebKeyboardFooterBottom, setMobileWebKeyboardFooterBottom] =
        useState(0);
    const isMobileWebKeyboardVisible =
        Platform.OS === 'web' &&
        !isNonMobileWeb &&
        mobileWebKeyboardFooterBottom > 0;

    // On mobile web the chat shell height is locked (see useMobileWebChatLock)
    // so the keyboard overlays the scroll content instead of resizing it. The
    // footer is portaled out of layout flow and floats above the keyboard via
    // `mobileWebKeyboardFooterBottom`, so that same offset has to be added to
    // the bottom padding or the scroll range never grows enough to reveal
    // content the keyboard is now covering.
    const {
        bottomPadding,
        onScrollViewLayout: rawOnScrollViewLayout,
        notifyContentHeight,
        naturalContentHeightRef,
        createAnchorOnLayout,
        notifyScrollOffset,
        scrollToAnchor,
    } = useTurnAnchoredScroll({
        scrollViewRef,
        keyboardOffset: mobileWebKeyboardFooterBottom,
        footerHeight,
    });

    const hasInitialScrollRef = useRef(false);
    const initialScrollDebounceRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const previousMessageCountRef = useRef(0);

    const showStaticWelcome = messages.length === 0 && !isStreaming;
    // The header mounts as soon as we know there are no messages, so it never
    // has to unmount/remount (and restart its entrance animation) once a
    // history restore resolves. Only the card list itself waits on the
    // restore, so a returning member never sees a frame of cards before
    // their existing thread loads in.
    // A `money-goal` deep-link silently answers the first question, so never show
    // the welcome menu overlay in that case (the member starts on the partner
    // question with no first-question chrome).
    const welcomeOverlayVisible =
        (showStaticWelcome || isHandingOffWelcome) &&
        !hasMoneyGoalDeepLink &&
        !deepLinkConsumed;
    const welcomeCarouselVisible = welcomeOverlayVisible && !isRestoringHistory;

    // The in-thread intro ("what best describes you?") only makes sense when the
    // member actually answered it — i.e. their answer renders as a bubble. A
    // silent (money-goal deep-link) answer strips to empty, so we hide the intro
    // and open directly on the partner question. Derived from message content so
    // it holds on history restore regardless of the URL param.
    const firstUserAnswerVisible = useMemo(() => {
        const firstUserMessage = messages.find((m) => m.role === 'user');
        if (firstUserMessage == null) {
            return false;
        }
        return getAssistantDisplayContent(firstUserMessage).some((part) => {
            if (part.type !== 'text') {
                return false;
            }
            const raw =
                typeof part.text === 'string'
                    ? part.text
                    : String(part.text ?? '');
            return formatUserMessageForDisplay(raw).trim() !== '';
        });
    }, [messages]);

    // Clear any leftover fade state if the user returns to chat (e.g. after Restart).
    useEffect(() => {
        setIsChatFadingOut(false);
    }, [setIsChatFadingOut]);

    const buildingPlanRecoverySentRef = useRef(false);

    // Restore building-plan state from message history (e.g. user navigated back).
    useEffect(() => {
        if (isBuildingPlan || messages.length === 0) return;

        if (hasAutoNavigateAhaMoment(messages)) {
            setIsBuildingPlan(true);
            setIsPlanReady(true);
            setIsChatInputLocked(true);
        }
    }, [messages.length]);

    // If bills were confirmed but show-intake-summary never arrived, silently
    // nudge the agent once so members aren't stuck on "putting your plan together".
    useEffect(() => {
        if (
            !isBuildingPlan ||
            isPlanReady ||
            isStreaming ||
            isRestoringHistory ||
            buildingPlanRecoverySentRef.current ||
            hasAutoNavigateAhaMoment(messages)
        ) {
            return undefined;
        }

        const timer = setTimeout(() => {
            if (
                buildingPlanRecoverySentRef.current ||
                hasAutoNavigateAhaMoment(messages)
            ) {
                return;
            }
            buildingPlanRecoverySentRef.current = true;
            track(
                AnalyticsUserEvent.CMM_BUILDING_PLAN_STUCK_RECOVERY,
                {
                    messageCount: messages.length,
                },
                /* sendImmediately */ true,
            );
            void onSendSilentMessage(BUILDING_PLAN_RECOVERY_MESSAGE, {
                showTypingIndicator: true,
            });
        }, BUILDING_PLAN_STUCK_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [
        isBuildingPlan,
        isPlanReady,
        isStreaming,
        isRestoringHistory,
        messages,
        onSendSilentMessage,
    ]);

    // Warm the goal card illustration cache early - the goal summary card is
    // typically shown several turns into the conversation, so prefetching it
    // now (rather than waiting for the widget to mount) hides the network
    // fetch behind the rest of the conversation on web.
    useEffect(() => {
        preloadGoalCardImages();
    }, []);

    const lastUserMessageId = [...messages]
        .reverse()
        .find((m) => m.role === 'user')?.id;

    // When a branch opener is active the response is entirely canned — hide
    // streaming state so the input isn't locked and the typing indicator stays off.
    const effectiveIsStreaming = openerActive ? false : isStreaming;

    const isTypingIndicatorActive =
        !openerActive &&
        ((effectiveIsStreaming && messages.length > 0) ||
            (showTypingIndicator &&
                !effectiveIsStreaming &&
                messages.length > 0));

    const [typingIndicatorMounted, setTypingIndicatorMounted] = useState(false);

    if (isTypingIndicatorActive && !typingIndicatorMounted) {
        setTypingIndicatorMounted(true);
    }

    const showTypingIndicatorComponent = typingIndicatorMounted;

    const logoIconTarget =
        showTypingIndicatorComponent || isBuildingPlan
            ? null
            : getLatestAssistantMessageLogoIconTarget(messages, isStreaming);

    const skipHistoricalTyping =
        messages.length > 1 &&
        !hasInitialScrollRef.current &&
        previousMessageCountRef.current === 0;

    const widgetHandlers = useMemo<ChatWidgetHandlers>(
        () => ({
            onSendSilentMessage,
            onSendMessageWithAgentPayload,
        }),
        [onSendSilentMessage, onSendMessageWithAgentPayload],
    );

    const scrollToBottom = useCallback((animated: boolean) => {
        scrollViewRef.current?.scrollToEnd({ animated });

        if (Platform.OS === 'web' && !animated) {
            requestAnimationFrame(() => {
                const node =
                    scrollViewRef.current as unknown as HTMLElement | null;
                if (node != null && 'scrollHeight' in node) {
                    node.scrollTop = node.scrollHeight;
                }
            });
        }
    }, []);

    const updateScrollToBottomVisibility = useCallback(() => {
        if (!hasInitialScrollRef.current) {
            setShowScrollToBottomButton(false);
            return;
        }

        const { offsetY, viewportHeight } = scrollMetricsRef.current;
        if (viewportHeight === 0) {
            return;
        }

        const usableViewportHeight = Math.max(0, viewportHeight - footerHeight);
        const maxScroll = Math.max(
            0,
            naturalContentHeightRef.current - usableViewportHeight,
        );
        const atBottom = offsetY >= maxScroll - SCROLL_TO_BOTTOM_THRESHOLD_PX;

        if (atBottom) {
            suppressScrollToBottomButtonRef.current = false;
            setShowScrollToBottomButton(false);
            return;
        }

        if (suppressScrollToBottomButtonRef.current) {
            return;
        }

        setShowScrollToBottomButton(true);
    }, [footerHeight]);

    useEffect(() => {
        updateScrollToBottomVisibility();
    }, [footerHeight, updateScrollToBottomVisibility]);

    const handleScrollToBottomPress = useCallback(() => {
        suppressScrollToBottomButtonRef.current = true;
        scrollToBottom(true);
        setShowScrollToBottomButton(false);
    }, [scrollToBottom]);

    const handleScrollViewLayout = useCallback(
        (height: number) => {
            scrollMetricsRef.current.viewportHeight = height;
            rawOnScrollViewLayout(height);
            updateScrollToBottomVisibility();
        },
        [rawOnScrollViewLayout, updateScrollToBottomVisibility],
    );

    const handleContentSizeChange = useCallback(
        (_width: number, contentHeight: number) => {
            suppressScrollToBottomButtonRef.current = false;
            scrollMetricsRef.current.contentHeight = contentHeight;
            updateScrollToBottomVisibility();
        },
        [updateScrollToBottomVisibility],
    );

    const handleChatScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset, contentSize, layoutMeasurement } =
                event.nativeEvent;

            if (
                contentOffset.y <
                scrollMetricsRef.current.offsetY - SCROLL_UP_DETECTION_PX
            ) {
                suppressScrollToBottomButtonRef.current = false;
            }

            scrollMetricsRef.current = {
                offsetY: contentOffset.y,
                contentHeight: contentSize.height,
                viewportHeight: layoutMeasurement.height,
            };
            updateScrollToBottomVisibility();
            notifyScrollOffset(contentOffset.y);

            const currentOffset = contentOffset.y;

            if (currentOffset > maxScrollOffsetRef.current) {
                maxScrollOffsetRef.current = currentOffset;
                return;
            }

            const scrollUpPx = maxScrollOffsetRef.current - currentOffset;
            if (scrollUpPx < SCROLL_UP_THRESHOLD_PX) {
                return;
            }

            pendingScrollUpPxRef.current = scrollUpPx;
            pendingScrollOffsetRef.current = currentOffset;

            if (scrollUpDebounceRef.current != null) {
                clearTimeout(scrollUpDebounceRef.current);
            }

            scrollUpDebounceRef.current = setTimeout(() => {
                track(AnalyticsUserEvent.AI_CHAT_SCROLLED_UP, {
                    scrollUpPx: pendingScrollUpPxRef.current,
                });
                maxScrollOffsetRef.current = pendingScrollOffsetRef.current;
                scrollUpDebounceRef.current = null;
            }, SCROLL_UP_DEBOUNCE_MS);
        },
        [updateScrollToBottomVisibility, notifyScrollOffset],
    );

    useEffect(() => {
        return () => {
            if (scrollUpDebounceRef.current != null) {
                clearTimeout(scrollUpDebounceRef.current);
            }
        };
    }, []);

    const scheduleInitialScrollToBottom = useCallback(() => {
        if (hasInitialScrollRef.current || messages.length === 0) {
            return;
        }

        scrollToBottom(false);

        if (initialScrollDebounceRef.current != null) {
            clearTimeout(initialScrollDebounceRef.current);
        }

        initialScrollDebounceRef.current = setTimeout(() => {
            hasInitialScrollRef.current = true;
            initialScrollDebounceRef.current = null;
        }, 200);
    }, [messages.length, scrollToBottom]);

    useLayoutEffect(() => {
        previousMessageCountRef.current = messages.length;
    }, [messages.length]);

    useEffect(() => {
        if (messages.length === 0 || hasInitialScrollRef.current) {
            return undefined;
        }

        scheduleInitialScrollToBottom();

        return () => {
            if (initialScrollDebounceRef.current != null) {
                clearTimeout(initialScrollDebounceRef.current);
            }
        };
    }, [messages.length, scheduleInitialScrollToBottom]);

    useEffect(() => {
        if (preRenderedWidgetProps == null) {
            setKeyboardHeight(0);
            return undefined;
        }

        const showEvent =
            Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
        const hideEvent =
            Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

        const showSubscription = Keyboard.addListener(showEvent, (event) => {
            setKeyboardHeight(event.endCoordinates.height);
        });
        const hideSubscription = Keyboard.addListener(hideEvent, () => {
            setKeyboardHeight(0);
        });

        return () => {
            showSubscription.remove();
            hideSubscription.remove();
        };
    }, [preRenderedWidgetProps]);

    useEffect(() => {
        if (showStaticWelcome && !hasLoggedFirstQuestionViewRef.current) {
            hasLoggedFirstQuestionViewRef.current = true;
            track(
                AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_VIEW_FIRST_QUESTION,
                {
                    flowEntryVariant: 'general',
                },
                /* sendImmediately */ true,
            );
            trackGrowthMarketingEvent(
                AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_VIEW_FIRST_QUESTION,
            );
        }
    }, [showStaticWelcome]);

    const activeDraftedButton = useMemo(
        () => getActiveDraftedMessageButtonWidget(messages),
        [messages],
    );

    const activeEstimatedBills = useMemo(
        () => (useBillsWidget ? getActiveEstimatedBillsWidget(messages) : null),
        [messages, useBillsWidget],
    );

    const activeSavingsGoalAmounts = useMemo(
        () =>
            useSavingsGoalsWidget
                ? getActiveSavingsGoalAmountsWidget(messages)
                : null,
        [messages, useSavingsGoalsWidget],
    );

    const showIncomeCollectionWidget =
        footerIncomeCollectionProps != null &&
        !effectiveIsStreaming &&
        assistantContentRevealed;

    const billsWidgetShownRef = useRef(false);
    const [billsDismissed, setBillsDismissed] = useState(false);
    const [billsMobileEditing, setBillsMobileEditing] = useState(false);
    const billsConfirmed =
        billsDismissed || (activeEstimatedBills?.confirmed ?? false);
    const showBillsWidget =
        activeEstimatedBills != null &&
        !billsConfirmed &&
        (billsWidgetShownRef.current || assistantContentRevealed);
    if (showBillsWidget) {
        billsWidgetShownRef.current = true;
    }
    if (activeEstimatedBills == null) {
        billsWidgetShownRef.current = false;
        if (billsDismissed) {
            setBillsDismissed(false);
        }
    }

    const prevShowBillsWidgetRef = useRef(false);
    useEffect(() => {
        if (showBillsWidget && !prevShowBillsWidgetRef.current) {
            scrollToAnchor(true);
        }
        prevShowBillsWidgetRef.current = showBillsWidget;
    }, [showBillsWidget, scrollToAnchor]);

    const savingsGoalsAmountsShownRef = useRef(false);
    const [savingsGoalsDismissed, setSavingsGoalsDismissed] = useState(false);
    const savingsGoalsConfirmed =
        savingsGoalsDismissed || (activeSavingsGoalAmounts?.confirmed ?? false);
    const showSavingsGoalAmountsWidget =
        activeSavingsGoalAmounts != null &&
        !savingsGoalsConfirmed &&
        (savingsGoalsAmountsShownRef.current || assistantContentRevealed);
    if (showSavingsGoalAmountsWidget) {
        savingsGoalsAmountsShownRef.current = true;
    }
    if (activeSavingsGoalAmounts == null) {
        savingsGoalsAmountsShownRef.current = false;
        if (savingsGoalsDismissed) {
            setSavingsGoalsDismissed(false);
        }
    }

    const prevShowSavingsGoalAmountsRef = useRef(false);
    useEffect(() => {
        if (
            showSavingsGoalAmountsWidget &&
            !prevShowSavingsGoalAmountsRef.current
        ) {
            scrollToAnchor(true);
        }
        prevShowSavingsGoalAmountsRef.current = showSavingsGoalAmountsWidget;
    }, [showSavingsGoalAmountsWidget, scrollToAnchor]);

    const handleResetThread = useCallback(() => {
        track(AnalyticsUserEvent.CMM_RESET_THREAD);
        buildingPlanRecoverySentRef.current = false;
        setIsChatInputLocked(false);
        setIsChatFadingOut(false);
        setIsBuildingPlan(false);
        setIsPlanReady(false);
        // Drop the captured first-question answer so "Start over" doesn't carry a
        // stale welcome selection into the next session's results.
        setWelcomeSelection(null);
        didHydrateWelcomeSelectionRef.current = false;

        if (onResetThread != null) {
            onResetThread();
            return;
        }

        const threadId = storage.getString(
            StorageKeys.CONVERSATIONAL_MM_THREAD_ID,
        );
        clearScheduledCmmAutoNavigate(threadId);
        storage.removeItem(StorageKeys.CONVERSATIONAL_MM_THREAD_ID);
        router.replace({ pathname: '/sign-up/get-started', params });
    }, [
        onResetThread,
        params,
        setIsChatInputLocked,
        setIsChatFadingOut,
        setIsBuildingPlan,
        setIsPlanReady,
        setWelcomeSelection,
    ]);

    const handleWelcomeStartSelect = useCallback(
        (
            answer: string,
            options?: {
                withHandoff?: boolean;
                silent?: boolean;
                welcomeOptionId?: string;
            },
        ) => {
            const withHandoff = options?.withHandoff ?? true;
            const silent = options?.silent ?? false;
            const welcomeOptionId = options?.welcomeOptionId;
            // A `silent` answer (money-goal deep-link) is saved as context only:
            // the member never saw the "what best describes you" question, so we
            // must not pretend they answered it. Empty display text + the payload
            // `silent` flag keep the answer out of the transcript, while it's
            // still persisted as member-notes context and commits the branch.
            const bubbleText = silent ? '' : answer;
            // Monopath: every "what best describes you" answer — option or
            // free-text — routes to the single goals-system flow. The member's
            // answer rides along as member-notes context (inputText); the fixed
            // `create-money-system-v2` marker drives the branch commit and the
            // goals-system opener, so all the existing welcome plumbing is reused.
            const rawPayload = buildMultiSelectConfirmedAgentMessage({
                displayText: bubbleText,
                payload: {
                    responseKey: CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY,
                    selectedOptionIds: [GOALS_SYSTEM_WELCOME_OPTION_ID],
                    inputText: answer,
                    ...(silent ? { silent: true } : {}),
                    // Drives the per-selection opener intro (and survives
                    // restore); absent for free-text ("something else") answers.
                    ...(welcomeOptionId != null ? { welcomeOptionId } : {}),
                },
            });
            const agentPayload = enrichWelcomePayload(rawPayload);

            // Capture the answer deterministically for the results object /
            // artifact (id + label for a pick, free-text otherwise) — independent
            // of the LLM's `memberNotes` summary, and even when `silent` (deep
            // link) suppresses the visible bubble.
            setWelcomeSelection(buildWelcomeSelection(answer, welcomeOptionId));

            // A `money-goal` deep-link auto-answers with no welcome overlay on
            // screen, so there's no carousel exit to clear the handoff flag —
            // skip it and let the first user bubble reveal via its scroll anchor.
            if (withHandoff) {
                setIsHandingOffWelcome(true);
            }
            hasPickedBranchRef.current = true;
            onWelcomeSubmit(agentPayload);

            track(
                AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_ANSWER_FIRST_QUESTION,
                { answerPayload: agentPayload },
                /* sendImmediately */ true,
            );
            trackGrowthMarketingEvent(
                AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_ANSWER_FIRST_QUESTION,
                { answerPayload: agentPayload },
            );

            const resolvedBranch = resolveWelcomeBranch(
                GOALS_SYSTEM_WELCOME_OPTION_ID,
            );
            const hasSyntheticOpener =
                getBranchOpener(GOALS_SYSTEM_WELCOME_OPTION_ID) != null;

            if (resolvedBranch != null && hasSyntheticOpener) {
                void onSetBranch(bubbleText, agentPayload, resolvedBranch, {
                    allowEmptyDisplay: silent,
                });
            } else {
                void onSendMessageWithAgentPayload(answer, agentPayload);
            }
        },
        [
            onWelcomeSubmit,
            onSendMessageWithAgentPayload,
            onSetBranch,
            setWelcomeSelection,
        ],
    );

    const handleWelcomeCarouselExitComplete = useCallback(() => {
        setIsHandingOffWelcome(false);
    }, []);

    // Re-hydrate the deterministic welcome selection on a restored thread. The
    // live commit sets it directly (the optimistic message carries no payload),
    // so this only fires after a refresh, reading the persisted `memberNotes`
    // payload from the restored messages.
    useEffect(() => {
        if (didHydrateWelcomeSelectionRef.current) return;
        const derived = deriveWelcomeSelectionFromMessages(messages);
        if (derived == null) return;
        didHydrateWelcomeSelectionRef.current = true;
        setWelcomeSelection(derived);
    }, [messages, setWelcomeSelection]);

    useEffect(() => {
        if (
            autoAnsweredMoneyGoalRef.current ||
            isRestoringHistory ||
            !hasMoneyGoalDeepLink ||
            moneyGoalParam == null
        ) {
            return;
        }
        autoAnsweredMoneyGoalRef.current = true;
        setDeepLinkConsumed(true);
        // Only auto-answer when starting fresh; a restored thread already holds
        // the saved answer, so we just clean up the URL below.
        if (messages.length === 0) {
            // Save the deep-linked goal as member-notes context, then drop the
            // member straight onto the partner question — without showing the
            // first question or a fabricated answer bubble.
            handleWelcomeStartSelect(resolveMoneyGoalAnswer(moneyGoalParam), {
                withHandoff: false,
                silent: true,
                welcomeOptionId: resolveMoneyGoalOptionId(moneyGoalParam),
            });
        }
        // Mark the deep link consumed. NOTE: the handler intentionally does NOT
        // strip the money-goal param from the URL — see handleDeepLinkConsumed
        // in get-started for why (stripping remounts the screen and orphans the
        // just-created thread, leaving every later turn stateless).
        onDeepLinkConsumed?.();
    }, [
        isRestoringHistory,
        messages.length,
        hasMoneyGoalDeepLink,
        moneyGoalParam,
        handleWelcomeStartSelect,
        onDeepLinkConsumed,
    ]);

    const openerStillAnimating = openerActive && !syntheticOpenerRevealed;

    const OPENER_BRANCH_MAP: Record<string, CMMBranch> = {
        'synthetic-opener-create-money-system-v2': CMMBranch.GoalsSystem,
        'synthetic-opener-start-investing': CMMBranch.StartInvesting,
    };

    const progressBarMetadata: ThreadMetricsSurfaceState | null = (() => {
        if (threadMetadata?.branch != null) {
            return threadMetadata;
        }
        if (syntheticOpenerMessage != null) {
            const branch = OPENER_BRANCH_MAP[syntheticOpenerMessage.id];
            if (branch != null) {
                return { branch, currentStep: null, collectedModules: [] };
            }
        }
        return threadMetadata ?? null;
    })();

    const isSingleSelect =
        preRenderedWidgetProps?.maxSelections === 1 &&
        !preRenderedWidgetProps?.allowCustomInput;

    const handleSelectWidgetSubmit = useCallback(
        (displayText: string, agentPayload: string) => {
            if (preRenderedWidgetProps == null) return;
            trackSelectInputSubmitted(
                preRenderedWidgetProps.responseKey,
                agentPayload,
                preRenderedWidgetProps.maxSelections === 1 &&
                    preRenderedWidgetProps.allowCustomInput !== true,
            );
            const parsed = parseMultiSelectConfirmedMessage(agentPayload);
            if (
                preRenderedWidgetProps.responseKey ===
                    'goalsDebt.hasHighInterestDebt' &&
                parsed?.selectedOptionIds.includes('no') === true
            ) {
                onAddCollectedModule?.(OnboardingChatModule.DebtCheck);
            }
            if (
                preRenderedWidgetProps.responseKey === 'goalsDebt.debtTypes' &&
                parsed?.selectedOptionIds.includes('no-high-interest-debt') ===
                    true
            ) {
                onAddCollectedModule?.(OnboardingChatModule.DebtCheck);
            }
            onFooterWidgetSubmit(agentPayload);
            void onSendMessageWithAgentPayload(displayText, agentPayload);
        },
        [
            preRenderedWidgetProps,
            onAddCollectedModule,
            onFooterWidgetSubmit,
            onSendMessageWithAgentPayload,
        ],
    );

    const isSavingsGoalTypeWidget =
        useSavingsGoalsWidget &&
        preRenderedWidgetProps?.responseKey === 'goalsSavings.goalType';

    const inputWidget =
        isSavingsGoalTypeWidget &&
        preRenderedWidgetProps != null &&
        !effectiveIsStreaming &&
        !openerStillAnimating &&
        assistantContentRevealed &&
        inputWidgetReady ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <SavingsGoalsWidget
                    options={[...preRenderedWidgetProps.options]}
                    maxSelections={3}
                    allowCustomInput={
                        preRenderedWidgetProps.allowCustomInput ?? true
                    }
                    customInputPlaceholder={
                        preRenderedWidgetProps.customInputPlaceholder
                    }
                    responseKey={preRenderedWidgetProps.responseKey}
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    onClassifyCustomGoal={onClassifyCustomSavingsGoal}
                    onSubmit={(displayText, agentPayload) => {
                        trackSelectInputSubmitted(
                            preRenderedWidgetProps.responseKey,
                            agentPayload,
                            false,
                        );
                        onFooterWidgetSubmit(agentPayload);
                        void onSendMessageWithAgentPayload(
                            displayText,
                            agentPayload,
                        );
                    }}
                />
            </YStack>
        ) : preRenderedWidgetProps != null &&
          isSingleSelect &&
          !effectiveIsStreaming &&
          !openerStillAnimating &&
          assistantContentRevealed &&
          inputWidgetReady ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <SingleSelectWidget
                    {...preRenderedWidgetProps}
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    onSubmit={handleSelectWidgetSubmit}
                />
            </YStack>
        ) : preRenderedWidgetProps != null &&
          !effectiveIsStreaming &&
          !openerStillAnimating &&
          assistantContentRevealed &&
          inputWidgetReady ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <MultiSelectInputWidget
                    {...preRenderedWidgetProps}
                    variant="footer"
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    isKeyboardVisible={isKeyboardVisible}
                    onSubmit={handleSelectWidgetSubmit}
                />
            </YStack>
        ) : footerDebtBalanceSlidersProps != null &&
          !effectiveIsStreaming &&
          !openerStillAnimating &&
          assistantContentRevealed &&
          inputWidgetReady ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <DebtBalanceSlidersWidget
                    {...footerDebtBalanceSlidersProps}
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    onSubmit={(displayText, agentPayload) => {
                        void onSendMessageWithAgentPayload(
                            displayText,
                            agentPayload,
                        );
                    }}
                />
            </YStack>
        ) : footerSliderProps != null &&
          !effectiveIsStreaming &&
          !openerStillAnimating &&
          assistantContentRevealed &&
          inputWidgetReady ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <SliderInputWidget
                    {...footerSliderProps}
                    variant="footer"
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    onSubmit={(displayText, agentPayload) => {
                        void onSendMessageWithAgentPayload(
                            displayText,
                            agentPayload,
                        );
                    }}
                />
            </YStack>
        ) : showBillsWidget && activeEstimatedBills != null ? (
            <YStack
                width="100%"
                alignItems="center"
                pointerEvents="auto"
                gap={12}
            >
                <YStack width="100%" paddingHorizontal="$4" alignItems="center">
                    <EstimatedBillsWidget
                        {...activeEstimatedBills.props}
                        disabled={disabled || effectiveIsStreaming}
                        isActive
                        onConfirm={(displayText, agentPayload) => {
                            track(
                                AnalyticsUserEvent.CMM_INPUT_CONFIRMATION_CONFIRMED,
                                {
                                    confirmationPrefix: 'bills-confirmed',
                                    messageCount: messages.length + 1,
                                },
                            );
                            setBillsDismissed(true);
                            void onSendMessageWithAgentPayload(
                                displayText,
                                agentPayload,
                            );
                        }}
                        onEditingChange={setBillsMobileEditing}
                    />
                </YStack>
                {!billsMobileEditing && (
                    <ChatInput
                        onSend={onSendMessage}
                        onStop={onStopStreaming}
                        isStreaming={effectiveIsStreaming}
                        disabled={disabled}
                        showResetButton={false}
                        onReset={handleResetThread}
                        compactBottom
                        inline
                    />
                )}
            </YStack>
        ) : showSavingsGoalAmountsWidget && activeSavingsGoalAmounts != null ? (
            <YStack
                width="100%"
                alignItems="center"
                pointerEvents="auto"
                gap={12}
            >
                <YStack width="100%" paddingHorizontal="$4" alignItems="center">
                    <SavingsGoalAmountsWidget
                        {...activeSavingsGoalAmounts.props}
                        disabled={disabled || effectiveIsStreaming}
                        isActive
                        onConfirm={(displayText, agentPayload) => {
                            setSavingsGoalsDismissed(true);
                            void onSendMessageWithAgentPayload(
                                displayText,
                                agentPayload,
                            );
                        }}
                    />
                </YStack>
                <ChatInput
                    onSend={onSendMessage}
                    onStop={onStopStreaming}
                    isStreaming={effectiveIsStreaming}
                    disabled={disabled}
                    showResetButton={false}
                    onReset={handleResetThread}
                    compactBottom
                    inline
                />
            </YStack>
        ) : showIncomeCollectionWidget &&
          footerIncomeCollectionProps != null ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <IncomeCollectionWidget
                    {...footerIncomeCollectionProps}
                    disabled={disabled || effectiveIsStreaming}
                    isActive
                    onSubmit={(displayText, agentPayload) => {
                        void onSendMessageWithAgentPayload(
                            displayText,
                            agentPayload,
                        );
                    }}
                />
            </YStack>
        ) : activeDraftedButton != null &&
          !showBillsWidget &&
          !showSavingsGoalAmountsWidget &&
          !showIncomeCollectionWidget &&
          !effectiveIsStreaming &&
          assistantContentRevealed ? (
            <YStack
                width="100%"
                paddingHorizontal="$4"
                alignItems="center"
                pointerEvents="auto"
            >
                <ConfirmationMenuWidget
                    {...activeDraftedButton.props}
                    disabled={disabled || effectiveIsStreaming}
                    isKeyboardVisible={isKeyboardVisible}
                    onConfirm={(displayText, agentPayload) => {
                        track(
                            AnalyticsUserEvent.CMM_INPUT_CONFIRMATION_CONFIRMED,
                            {
                                confirmationPrefix:
                                    activeDraftedButton.props
                                        .confirmationPrefix,
                                messageCount: messages.length + 1,
                            },
                        );
                        void onSendMessageWithAgentPayload(
                            displayText,
                            agentPayload,
                        );
                    }}
                    onChangeRequest={(message) => {
                        track(
                            AnalyticsUserEvent.CMM_INPUT_CONFIRMATION_CHANGE_REQUESTED,
                            {
                                confirmationPrefix:
                                    activeDraftedButton.props
                                        .confirmationPrefix,
                                messageCount: messages.length + 1,
                            },
                        );
                        onSendMessage(message);
                    }}
                />
            </YStack>
        ) : (
            <ChatInput
                onSend={onSendMessage}
                onStop={onStopStreaming}
                isStreaming={effectiveIsStreaming}
                disabled={disabled}
                showResetButton={false}
                onReset={handleResetThread}
                compactBottom
                inline
                showEditIcon
            />
        );

    const footerFadeStyle = {
        opacity: isChatFadingOut ? 0 : 1,
        transition: `opacity ${CHAT_FADE_OUT_MS}ms ease`,
    } as const;

    const footer = (
        <YStack width="100%" alignItems="center" style={footerFadeStyle}>
            {inputWidget}
            {!isMobileWebKeyboardVisible && (
                <View
                    onLayout={(event) => {
                        setDisclosureHeight(event.nativeEvent.layout.height);
                    }}
                >
                    <LabelRegular
                        color="$black"
                        textAlign="center"
                        paddingTop="$3"
                        paddingBottom="$3"
                    >
                        {ONBOARDING_AI_DISCLOSURE}
                    </LabelRegular>
                </View>
            )}
        </YStack>
    );

    return (
        <YStack
            flex={1}
            minHeight={0}
            position="relative"
            opacity={isChatFadingOut ? 0 : 1}
            style={{
                transition: `opacity ${CHAT_FADE_OUT_MS}ms ease`,
            }}
            backgroundColor="transparent"
        >
            {/* Dev-only convenience: a "Start over" pinned in the top corner so
                testers can reset without scrolling to the input. Hidden in
                production; the input keeps its own reset button everywhere. */}
            {FRUITFUL_ENV !== 'production' &&
                onResetThread != null &&
                messages.length > 0 && (
                    <View
                        position="absolute"
                        top={6}
                        right={6}
                        zIndex={100}
                        backgroundColor="$white"
                        borderRadius={999}
                        borderWidth={1}
                        borderColor="$gray300"
                        pointerEvents="auto"
                    >
                        <NewChatButton onPress={handleResetThread} />
                    </View>
                )}
            <OnboardingChatProgressBar
                threadMetadata={progressBarMetadata}
                userMessageCount={
                    messages.filter((m) => m.role === 'user').length
                }
            />
            <ChatMessageLayout
                scrollViewRef={scrollViewRef}
                onScroll={handleChatScroll}
                onScrollViewLayout={handleScrollViewLayout}
                onContentSizeChange={handleContentSizeChange}
                onFooterLayout={setFooterHeight}
                onMobileWebKeyboardFooterBottomChange={
                    setMobileWebKeyboardFooterBottom
                }
                keyboardOverlapsContent
                reservedFooterHeight={
                    isMobileWebKeyboardVisible ? 0 : disclosureHeight
                }
                footer={showStaticWelcome ? null : footer}
            >
                <YStack>
                    <View
                        onLayout={(event) => {
                            notifyContentHeight(
                                event.nativeEvent.layout.height,
                            );
                        }}
                    >
                        <YStack gap="$4">
                            {!welcomeOverlayVisible && messages.length > 0 && (
                                <View paddingTop="$6" gap="$4">
                                    <WelcomeMessage
                                        skipAnimation
                                        centered={false}
                                    />
                                    {firstUserAnswerVisible && (
                                        <AssistantMessage
                                            content={WELCOME_INTRO_MESSAGE}
                                            skipTypingAnimation
                                            showLogoIcon={false}
                                        />
                                    )}
                                </View>
                            )}
                            {messages.map((message, messageIndex) => {
                                const contentParts =
                                    getAssistantDisplayContent(message);

                                if (message.role === 'user') {
                                    const isFirstUserMessage =
                                        messageIndex === 0;
                                    const userBubbles = contentParts.map(
                                        (content, index) => {
                                            const contentKey = `${message.id}-${content.type}-${index.toString()}`;
                                            if (content.type !== 'text')
                                                return null;

                                            const textValue =
                                                typeof content.text === 'string'
                                                    ? content.text
                                                    : String(
                                                          content.text ?? '',
                                                      );
                                            const displayValue =
                                                formatUserMessageForDisplay(
                                                    textValue,
                                                );
                                            // Skip messages that display as empty
                                            // — including silent (money-goal)
                                            // answers whose payload strips to
                                            // nothing — so no empty bubble renders.
                                            if (displayValue.trim() === '')
                                                return null;

                                            const isScrollAnchor =
                                                message.id ===
                                                    lastUserMessageId &&
                                                index === 0;

                                            const isFreshlyPickedFirstAnswer =
                                                hasPickedBranchRef.current &&
                                                isFirstUserMessage;
                                            // Keeps this element mounted (rather
                                            // than swapping in later) so the
                                            // typewriter never remounts mid-sequence.
                                            const isWaitingForAnchorScroll =
                                                isFreshlyPickedFirstAnswer &&
                                                (isHandingOffWelcome ||
                                                    !firstAnswerRevealed);

                                            const bubble = (
                                                <UserMessageBubble
                                                    text={displayValue}
                                                    isError={
                                                        message.isError ?? false
                                                    }
                                                    typewriter={
                                                        isFreshlyPickedFirstAnswer
                                                    }
                                                    typewriterEnabled={
                                                        !isWaitingForAnchorScroll
                                                    }
                                                />
                                            );

                                            if (isScrollAnchor) {
                                                return (
                                                    <View
                                                        key={contentKey}
                                                        opacity={
                                                            isWaitingForAnchorScroll
                                                                ? 0
                                                                : 1
                                                        }
                                                        onLayout={
                                                            isHandingOffWelcome
                                                                ? undefined
                                                                : createAnchorOnLayout(
                                                                      message.id,
                                                                      isFreshlyPickedFirstAnswer
                                                                          ? () => {
                                                                                setFirstAnswerRevealed(
                                                                                    true,
                                                                                );
                                                                            }
                                                                          : undefined,
                                                                  )
                                                        }
                                                    >
                                                        {bubble}
                                                    </View>
                                                );
                                            }

                                            return (
                                                <React.Fragment
                                                    key={contentKey}
                                                >
                                                    {bubble}
                                                </React.Fragment>
                                            );
                                        },
                                    );

                                    if (
                                        isFirstUserMessage &&
                                        syntheticOpenerMessage != null
                                    ) {
                                        const isRestoredSession =
                                            !hasPickedBranchRef.current;
                                        const contentReady =
                                            isRestoredSession ||
                                            syntheticContentReady;

                                        const syntheticParts = contentReady
                                            ? getAssistantDisplayContent(
                                                  syntheticOpenerMessage,
                                              )
                                            : [];
                                        const hasRealAssistantMessages =
                                            messages.some(
                                                (m) => m.role === 'assistant',
                                            );
                                        const syntheticLogoTarget =
                                            hasRealAssistantMessages ||
                                            syntheticParts.length === 0
                                                ? null
                                                : {
                                                      messageId:
                                                          syntheticOpenerMessage.id,
                                                      contentIndex:
                                                          syntheticParts.length -
                                                          1,
                                                  };
                                        return (
                                            <React.Fragment key={message.id}>
                                                {userBubbles}
                                                <SequentialAssistantContent
                                                    message={
                                                        syntheticOpenerMessage
                                                    }
                                                    contentParts={
                                                        syntheticParts
                                                    }
                                                    logoIconTarget={
                                                        syntheticLogoTarget
                                                    }
                                                    isStreaming={false}
                                                    widgetHandlers={
                                                        widgetHandlers
                                                    }
                                                    skipTypingAnimation={
                                                        isRestoredSession ||
                                                        skipHistoricalTyping
                                                    }
                                                    onAllPartsRevealed={() => {
                                                        setSyntheticOpenerRevealed(
                                                            true,
                                                        );
                                                        setAssistantContentRevealed(
                                                            true,
                                                        );
                                                    }}
                                                />
                                            </React.Fragment>
                                        );
                                    }

                                    // The confirmed bills total is shown inline
                                    // as the member's own message bubble (e.g.
                                    // "Estimated bills: $2,715"), so we no longer
                                    // render a separate bills recap here.
                                    return userBubbles;
                                }

                                // The agent's first assistant response duplicates
                                // the synthetic opener content. Suppress it only when
                                // it's the direct reply to the branch-selection message
                                // (i.e. no second user message has been sent yet).
                                if (syntheticOpenerMessage != null) {
                                    const preceding = messages.slice(
                                        0,
                                        messageIndex,
                                    );
                                    const isFirstAssistantMsg = preceding.every(
                                        (m) => m.role !== 'assistant',
                                    );
                                    const userMsgCount = preceding.filter(
                                        (m) => m.role === 'user',
                                    ).length;
                                    if (
                                        isFirstAssistantMsg &&
                                        userMsgCount <= 1
                                    ) {
                                        return null;
                                    }
                                }

                                return (
                                    <SequentialAssistantContent
                                        key={message.id}
                                        message={message}
                                        contentParts={contentParts}
                                        logoIconTarget={logoIconTarget}
                                        isStreaming={isStreaming}
                                        widgetHandlers={widgetHandlers}
                                        skipTypingAnimation={
                                            skipHistoricalTyping
                                        }
                                        suppressLogoIcon={isBuildingPlan}
                                        onContentPartRendered={
                                            trackAgentResponsePartRendered
                                        }
                                        onAllPartsRevealed={
                                            messageIndex === messages.length - 1
                                                ? () => {
                                                      setAssistantContentRevealed(
                                                          true,
                                                      );
                                                  }
                                                : undefined
                                        }
                                    />
                                );
                            })}
                            {isBuildingPlan ? (
                                <AssistantMessage
                                    content="Perfect. I've got everything I need, and I'm putting your plan together now."
                                    showLogoIcon={isPlanReady}
                                    skipTypingAnimation={isPlanReady}
                                />
                            ) : null}
                            {isBuildingPlan && !isPlanReady ? (
                                <TypingIndicator isActive />
                            ) : !isBuildingPlan &&
                              showTypingIndicatorComponent ? (
                                <TypingIndicator
                                    isActive={isTypingIndicatorActive}
                                />
                            ) : null}
                        </YStack>
                    </View>
                    <View
                        style={{
                            height:
                                showBillsWidget || showSavingsGoalAmountsWidget
                                    ? Math.max(
                                          bottomPadding,
                                          footerHeight - disclosureHeight + 16,
                                          100,
                                      )
                                    : bottomPadding,
                        }}
                    />
                </YStack>
            </ChatMessageLayout>
            {welcomeOverlayVisible && (
                <View
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={footerHeight}
                    pointerEvents="box-none"
                >
                    <WelcomeStartMenu
                        header={
                            <WelcomeMessage
                                skipAnimation={false}
                                centered={false}
                            />
                        }
                        showMenu={welcomeCarouselVisible}
                        onSelect={(answer, optionId) => {
                            handleWelcomeStartSelect(answer, {
                                welcomeOptionId: optionId,
                            });
                        }}
                        onExitComplete={handleWelcomeCarouselExitComplete}
                        keyboardOffset={mobileWebKeyboardFooterBottom}
                    />
                </View>
            )}
            {showScrollToBottomButton && messages.length > 0 && (
                <View
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={footerHeight + SCROLL_TO_BOTTOM_BUTTON_GAP}
                    alignItems="center"
                    pointerEvents="box-none"
                >
                    <ScrollToBottomButton onPress={handleScrollToBottomPress} />
                </View>
            )}
        </YStack>
    );
};

const WELCOME_TITLE =
    'Welcome to Fruitful! Let\u2019s build your money system.';
const WELCOME_BODY = 'Takes about 3 minutes';

const WELCOME_ENTRANCE_MS = 500;
const WELCOME_RISE_PX = 12;
const WELCOME_WINDOWS = {
    title: [0, 0.6] as const,
    body: [0.25, 1] as const,
};

const AnimatedYStack = Animated.createAnimatedComponent(YStack);

const WelcomeEntranceItem = ({
    entrance,
    start,
    end,
    children,
}: {
    entrance: SharedValue<number>;
    start: number;
    end: number;
    children: React.ReactNode;
}) => {
    const style = useAnimatedStyle(() => {
        const raw = (entrance.value - start) / (end - start);
        const clamped = raw < 0 ? 0 : raw > 1 ? 1 : raw;
        const eased = 1 - (1 - clamped) ** 3;
        return {
            opacity: eased,
            transform: [{ translateY: (1 - eased) * WELCOME_RISE_PX }],
        };
    }, [start, end]);

    return <AnimatedYStack style={style}>{children}</AnimatedYStack>;
};

const WelcomeMessage = ({
    skipAnimation = false,
    centered = false,
}: {
    skipAnimation?: boolean;
    centered?: boolean;
}) => {
    const entrance = useSharedValue(skipAnimation ? 1 : 0);

    useEffect(() => {
        if (skipAnimation) return;
        entrance.value = withTiming(1, {
            duration: WELCOME_ENTRANCE_MS,
            easing: Easing.linear,
        });
    }, [skipAnimation, entrance]);

    return (
        <YStack
            paddingHorizontal={CHAT_ASSISTANT_PADDING_HORIZONTAL}
            gap={24}
            alignItems={centered ? 'center' : 'flex-start'}
        >
            <WelcomeAnimation />
            <YStack gap={4} alignItems={centered ? 'center' : 'flex-start'}>
                <WelcomeEntranceItem
                    entrance={entrance}
                    start={WELCOME_WINDOWS.title[0]}
                    end={WELCOME_WINDOWS.title[1]}
                >
                    <H3Regular textAlign={centered ? 'center' : 'left'}>
                        {WELCOME_TITLE}
                    </H3Regular>
                </WelcomeEntranceItem>
                <WelcomeEntranceItem
                    entrance={entrance}
                    start={WELCOME_WINDOWS.body[0]}
                    end={WELCOME_WINDOWS.body[1]}
                >
                    <BodySmallRegular
                        color="$gray1000"
                        textAlign={centered ? 'center' : 'left'}
                    >
                        {WELCOME_BODY}
                    </BodySmallRegular>
                </WelcomeEntranceItem>
            </YStack>
        </YStack>
    );
};
